import { FC, useMemo } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Checkbox, Grid } from '@ts/storybook';
import Misc from '@components/Misc';
import { IForm } from '@ts/ui-models';

interface Row {
  id: string;
  name: string;
  value: (disabled: boolean, values: any, data?: any) => React.ReactNode;
  lockable?: boolean;
  readable?: boolean;
  mainable?: boolean;
  seriable?: boolean;
}

interface IGeneralTabFieldsTableProps extends WithTranslation, IForm {
  rows?: Row[];
  disabled?: boolean;
  hasUnique?: boolean;
  fDisableMainFlavor?: (attrPath: string, attr: string) => boolean;
  fnCheckBoxValue: (attrPath: string, attr: string) => boolean;
  fnOnChangeCheckBox: (data: { attrPath: string; attr: string }, value: boolean) => void;
}

const GeneralTabFieldsTable: FC<IGeneralTabFieldsTableProps> = ({
  t,
  mode,
  rows = [],
  values,
  disabled = false,
  hasUnique,
  fDisableMainFlavor,
  fnCheckBoxValue,
  fnOnChangeCheckBox,
}) => {
  const disabledInEdit = mode === 'edit';

  const showLocked = useMemo(() => rows.some(row => row.lockable), [rows]);
  const showReadOnly = useMemo(() => rows.some(row => row.readable), [rows]);
  const showMainFlavor = useMemo(() => rows.some(row => row.mainable), [rows]);

  return (
    <Grid container spacing={3} mt={3}>
      <Misc.NewPaperWithPadding>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('Name')}</TableCell>
              <TableCell>{t('Value')}</TableCell>
              {showMainFlavor && <TableCell>{t('Main Flavor')}</TableCell>}
              {showLocked && <TableCell>{t('Locked to item type')}</TableCell>}
              {showReadOnly && <TableCell>{t('Read only')}</TableCell>}
              {hasUnique && (
                <>
                  <TableCell>{t('Unique ID series')}</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id}>
                <TableCell component="th" scope="row">
                  {row.name}
                </TableCell>
                <TableCell>{row.value(disabled, values)}</TableCell>
                {showMainFlavor && row.hasOwnProperty('mainable') && (
                  <TableCell>
                    <Checkbox
                      id={`checkbox-mainFlavor-${row.id}`}
                      checked={fnCheckBoxValue('mainFlavor', row.id)}
                      onChangeValue={v => fnOnChangeCheckBox({ attrPath: 'mainFlavor', attr: row.id }, v)}
                      color="primary"
                      disabled={disabled || disabledInEdit || !row.mainable || fDisableMainFlavor?.('mainFlavor', row.id)}
                      value={row.id}
                    />
                  </TableCell>
                )}
                {showLocked && row.hasOwnProperty('lockable') && (
                  <TableCell>
                    <Checkbox
                      checked={fnCheckBoxValue('attrsLocked', row.id)}
                      onChangeValue={v => fnOnChangeCheckBox({ attrPath: 'attrsLocked', attr: row.id }, v)}
                      color="primary"
                      disabled={disabled || !row.lockable}
                      value={row.id}
                    />
                  </TableCell>
                )}
                {showReadOnly && row.hasOwnProperty('readable') && (
                  <TableCell>
                    <Checkbox
                      checked={fnCheckBoxValue('attrsReadOnly', row.id)}
                      onChangeValue={v => fnOnChangeCheckBox({ attrPath: 'attrsReadOnly', attr: row.id }, v)}
                      color="primary"
                      disabled={disabled || !row.readable}
                      value={row.id}
                    />
                  </TableCell>
                )}
                {hasUnique && row.hasOwnProperty('seriable') && (
                  <TableCell>
                    <Checkbox
                      checked={fnCheckBoxValue('attrsSeriable', row.id)}
                      onChangeValue={v => fnOnChangeCheckBox({ attrPath: 'attrsSeriable', attr: row.id }, v)}
                      color="primary"
                      disabled={disabled || !row.seriable}
                      value={row.id}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Misc.NewPaperWithPadding>
    </Grid>
  );
};

export default withTranslation()(GeneralTabFieldsTable);
